import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, IconButton, Tooltip, Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import * as C from '../../theme/colors.constants';
import MarcadoresCoresProcesso from './Sinalizacao/MarcadoresCoresProcesso';
import MarcadoresIconesProcesso from './Sinalizacao/MarcadoresIconesProcesso';
import AcaoReenviarWhatsappProcesso from './Acoes/AcaoReenviarWhatsappProcesso';
import AcaoRequisicaoProcesso from './Acoes/AcaoRequisicaoProcesso';
import AcaoEnviarEmailDevedorProcesso from './Acoes/AcaoEnviarEmailDevedorProcesso';
import { findInformacoesAdicionaisInpescaoProcessoService } from '../../../services/core/processos/processosService';

/**
 * Espelha `podeAtenderRequisicao` de `inspecaoProcessoController.js` (tela
 * real, AW2-912) — nome enganoso preservado: retorna `true` quando o botão
 * deve ficar DESABILITADO, não quando o usuário "pode atender".
 */
function podeAtenderRequisicaoDesabilitado(sinalizacaoInfo) {
  return !(
    sinalizacaoInfo?.podeModificarFase
    || (!sinalizacaoInfo?.podeModificarFase && sinalizacaoInfo?.temPermissaoAtenderRequisicao)
  );
}

/**
 * Barra de ações acima do painel de detalhe.
 *
 * WhatsApp, bandeira e e-mail (AW2-912) acionam os 3 painéis reais de ação
 * do processo (`Acoes/`), mutuamente exclusivos — abrir um fecha o outro
 * aberto, clicar no já aberto fecha (replica `changeActionButton` da tela
 * real). Os 9 sinalizadores reais do processo (AW2-911) e o botão de copiar
 * completam a barra.
 */
export default function BarraAcoesProcesso({
  idProcesso, sinalizacaoInfo, setSinalizacaoInfo, requisicoesOptions,
}) {
  const [acaoAtiva, setAcaoAtiva] = useState(null);
  // "Latest ref" — mutado direto no corpo (não em efeito) de propósito, para
  // que aoSucesso sempre veja o idProcesso mais recente ao resolver, mesmo
  // que o processo tenha mudado enquanto o refetch estava em voo.
  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  useEffect(() => {
    setAcaoAtiva(null);
  }, [idProcesso]);

  const alternarAcao = useCallback((tag) => {
    setAcaoAtiva(atual => (atual === tag ? null : tag));
  }, []);

  const aoSucesso = useCallback(() => {
    const alvo = idProcesso;
    setAcaoAtiva(null);

    if (!alvo) {
      return;
    }

    findInformacoesAdicionaisInpescaoProcessoService(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setSinalizacaoInfo(data);
        }
      })
      .catch(() => {});
  }, [idProcesso, setSinalizacaoInfo]);

  const whatsappDesabilitado = (sinalizacaoInfo?.podeReenviarWhatsapp?.length ?? 0) === 0;
  const requisicaoDesabilitada = podeAtenderRequisicaoDesabilitado(sinalizacaoInfo);
  const emailDesabilitado = !sinalizacaoInfo?.podeModificarFase;
  const labelRequisicao = sinalizacaoInfo?.temRequisicao ? 'Atender Requisição' : 'Gerar Requisição';

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap', mb: 1, px: 0.5,
      }}
      >
        <Tooltip title="Reenviar WhatsApp" arrow>
          <span>
            <IconButton
              size="small"
              sx={{ color: C.navy }}
              disabled={whatsappDesabilitado}
              onClick={() => alternarAcao('whatsapp')}
            >
              <WhatsAppIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={labelRequisicao} arrow>
          <span>
            <IconButton
              size="small"
              sx={{ color: C.navy }}
              disabled={requisicaoDesabilitada}
              onClick={() => alternarAcao('requisicao')}
            >
              <FlagOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Enviar e-mail ao Devedor" arrow>
          <span>
            <IconButton
              size="small"
              sx={{ color: C.navy }}
              disabled={emailDesabilitado}
              onClick={() => alternarAcao('email')}
            >
              <EmailOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.5 }} />

        <MarcadoresCoresProcesso
          idProcesso={idProcesso}
          sinalizacaoCoresQuery={sinalizacaoInfo?.sinalizacaoCoresQuery}
          permitirAjuizarProcesso={sinalizacaoInfo?.permitirAjuizarProcesso}
          podeModificarFase={sinalizacaoInfo?.podeModificarFase}
          setSinalizacaoInfo={setSinalizacaoInfo}
        />

        <MarcadoresIconesProcesso
          idProcesso={idProcesso}
          sinalizacaoClienteProcessoQuery={sinalizacaoInfo?.sinalizacaoClienteProcessoQuery}
          podeModificarFase={sinalizacaoInfo?.podeModificarFase}
          setSinalizacaoInfo={setSinalizacaoInfo}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.5 }} />

        <Tooltip title="Copiar número do processo" arrow>
          <IconButton size="small" sx={{ color: C.navy }} onClick={() => navigator.clipboard.writeText(idProcesso)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {acaoAtiva === 'whatsapp' && (
        <AcaoReenviarWhatsappProcesso
          idProcesso={idProcesso}
          idFaseAtual={sinalizacaoInfo?.idFase}
          podeReenviarWhatsapp={sinalizacaoInfo?.podeReenviarWhatsapp}
          onSucesso={aoSucesso}
        />
      )}
      {acaoAtiva === 'requisicao' && (
        <AcaoRequisicaoProcesso
          idProcesso={idProcesso}
          requisicoesOptions={requisicoesOptions}
          onSucesso={aoSucesso}
        />
      )}
      {acaoAtiva === 'email' && (
        <AcaoEnviarEmailDevedorProcesso
          idProcesso={idProcesso}
          onSucesso={aoSucesso}
        />
      )}
    </>
  );
}

BarraAcoesProcesso.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sinalizacaoInfo: PropTypes.object,
  setSinalizacaoInfo: PropTypes.func.isRequired,
  requisicoesOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.string,
  })),
};

BarraAcoesProcesso.defaultProps = {
  idProcesso: null,
  sinalizacaoInfo: null,
  requisicoesOptions: [],
};
